import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Menggabungkan class Tailwind dengan penyelesaian konflik.
 *
 * `twMerge` memastikan class yang datang belakangan menang, sehingga
 * komponen bisa menerima override lewat prop `className`.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Format rupiah, mis. 1500000 → "Rp1.500.000". */
export function formatCurrency(value: number | string): string {
  const amount = typeof value === 'string' ? Number(value) : value;

  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(amount) ? amount : 0);
}

/** Format tanggal Indonesia, mis. "20 Juli 2026". */
export function formatDate(value: string | Date, withTime = false): string {
  const date = typeof value === 'string' ? new Date(value) : value;

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat('id-ID', {
    dateStyle: 'long',
    ...(withTime ? { timeStyle: 'short' } : {}),
  }).format(date);
}

/** Usia dari tanggal lahir, mis. "4 th 3 bln" (dihitung sampai hari ini). */
export function formatAge(birthDate: string | Date): string {
  const date = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const now = new Date();
  let years = now.getFullYear() - date.getFullYear();
  let months = now.getMonth() - date.getMonth();

  if (now.getDate() < date.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  if (years < 0) {
    return '-';
  }

  return `${years} th ${months} bln`;
}

/** Inisial untuk avatar, maksimal dua huruf. */
export function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

const ROOT_DOMAIN = 'sipaud.com';
/** Subdomain yang bukan milik tenant mana pun. */
const RESERVED_SUBDOMAINS = new Set(['app', 'api', 'www', 'storage']);

/**
 * Slug tenant dari subdomain saat ini (mis. "tk-tunas-bangsa" dari
 * tk-tunas-bangsa.sipaud.com), atau null bila diakses lewat app.sipaud.com,
 * localhost, IP, atau domain lain — dipakai halaman login untuk menampilkan
 * branding lembaga. Sekadar tampilan; identitas tenant sesungguhnya tetap
 * dari token setelah masuk, bukan dari subdomain ini.
 */
/**
 * Alamat Portal Wali Murid satu lembaga — yang dibagikan admin ke orang tua.
 *
 * Dibuka dari subdomain lembaga, halaman masuk mengenali lembaganya sendiri
 * lewat `getTenantSlugFromHost()` di bawah, sehingga wali tidak perlu mengetik
 * kode sekolah sama sekali: cukup NIS dan kata sandi, dengan nama & logo
 * satuannya terpampang. Itulah sebabnya yang dibagikan sebaiknya tautan ini,
 * bukan instruksi "isi kode sekolah".
 *
 * Di luar produksi (localhost, IP, domain lain) subdomain tenant tidak ada,
 * jadi yang dikembalikan adalah alamat masuk generik di host yang sedang
 * dipakai — tautan yang benar-benar bisa dibuka saat itu, meski kode
 * sekolahnya masih harus diketik.
 */
export function tenantPortalUrl(
  slug: string,
  location: { protocol: string; host: string; hostname: string } = window.location,
): string {
  const isProduction =
    location.hostname === ROOT_DOMAIN || location.hostname.endsWith(`.${ROOT_DOMAIN}`);

  return isProduction
    ? `https://${slug}.${ROOT_DOMAIN}/masuk`
    : `${location.protocol}//${location.host}/masuk`;
}

export function getTenantSlugFromHost(hostname: string = window.location.hostname): string | null {
  if (!hostname.endsWith(`.${ROOT_DOMAIN}`)) {
    return null;
  }

  const subdomain = hostname.slice(0, -(ROOT_DOMAIN.length + 1));

  if (!subdomain || subdomain.includes('.') || RESERVED_SUBDOMAINS.has(subdomain)) {
    return null;
  }

  return subdomain;
}

/**
 * Lama uji coba dalam bahasa yang dipakai sekolah.
 *
 * 180 hari dibaca "1 semester", bukan "180 hari": sekolah berpikir dalam
 * semester, dan angka hari yang besar justru terasa seperti syarat yang
 * disembunyikan. Kelipatan 30 hari lainnya menjadi bulan; sisanya hari.
 */
export function formatTrialDuration(days: number): string {
  if (days >= 180 && days % 180 === 0) {
    return `${days / 180} semester`;
  }

  if (days >= 60 && days % 30 === 0) {
    return `${days / 30} bulan`;
  }

  return `${days} hari`;
}

/**
 * Setara per bulan dari harga tahunan, dibulatkan ke ratusan rupiah.
 *
 * Langganan hanya tahunan, tetapi angka yang paling mudah ditimbang sekolah
 * adalah angka bulanan — "Rp20.000/bulan" lebih jujur menggambarkan bebannya
 * daripada "Rp240.000" yang terbaca sekaligus. Tetap jelas ditulis "dibayar
 * tahunan" di sebelahnya: ini perbandingan, bukan pilihan cara bayar.
 */
export function monthlyEquivalent(priceYearly: number): number {
  return Math.round(priceYearly / 12 / 100) * 100;
}

/**
 * Tawaran uji coba dalam bahasa sekolah: "sampai 31 Desember 2026" bila
 * paketnya bertanggal tetap (`trialUntil`), selain itu lamanya ("1 semester",
 * "30 hari"). Tanggal dibaca menurut WIB, zona tempat batas itu ditetapkan.
 */
export function formatTrialOffer(trialDays: number, trialUntil: string | null | undefined): string {
  if (trialUntil) {
    const date = new Intl.DateTimeFormat('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Asia/Jakarta',
    }).format(new Date(trialUntil));
    return `sampai ${date}`;
  }

  return formatTrialDuration(trialDays);
}
